/**
 * JoinOrgSheet -- search for an existing organization and request to join.
 * Modal bottom sheet with debounced search input + results list.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { View, Text, ScrollView as RNScrollView, TextInput as RNTextInput } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { LiquidCanvas } from '@/components/ui/LiquidBackdrop'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import { Search, Building2, ShieldCheck, Users, X } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Skeleton } from '@/components/ui/Skeleton'
import { organizationService } from '@/lib/services/organization.service'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import type { Organization } from '@/lib/types/organization.types'

interface JoinOrgSheetProps {
  visible: boolean
  onClose: () => void
  onJoined?: (orgId: string) => void
  initialSearchQuery?: string
}

export function JoinOrgSheet({
  visible,
  onClose,
  onJoined,
  initialSearchQuery,
}: JoinOrgSheetProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const inputRef = useRef<RNTextInput>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [query, setQuery] = useState(initialSearchQuery ?? '')
  const [results, setResults] = useState<Organization[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [isJoining, setIsJoining] = useState(false)

  // Focus input on open
  useEffect(() => {
    if (!visible) return
    const timer = setTimeout(() => inputRef.current?.focus(), 300)
    return () => clearTimeout(timer)
  }, [visible])

  // Run initial search if query provided
  useEffect(() => {
    if (visible && initialSearchQuery) {
      void handleSearch(initialSearchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialSearchQuery])

  const handleSearch = useCallback(async (text: string) => {
    if (!text.trim()) {
      setResults([])
      setHasSearched(false)
      return
    }
    setIsSearching(true)
    try {
      const data = await organizationService.findByLegalName(text)
      setResults(data)
      setHasSearched(true)
    } catch {
      setResults([])
      setHasSearched(true)
    } finally {
      setIsSearching(false)
    }
  }, [])

  const handleQueryChange = useCallback(
    (text: string) => {
      setQuery(text)
      if (timerRef.current) clearTimeout(timerRef.current)
      if (!text.trim()) {
        setResults([])
        setIsSearching(false)
        setHasSearched(false)
        return
      }
      setIsSearching(true)
      timerRef.current = setTimeout(() => handleSearch(text), 250)
    },
    [handleSearch],
  )

  // Alert.alert is an empty no-op on react-native-web, so on app.tago.rent
  // tapping an organisation did nothing — the whole join flow was unreachable.
  const [pendingOrg, setPendingOrg] = useState<Organization | null>(null)

  const handleJoinRequest = useCallback((org: Organization) => {
    impact(ImpactStyle.Light)
    setPendingOrg(org)
  }, [])

  const confirmJoinRequest = useCallback(async () => {
    const org = pendingOrg
    if (!org) return
    setPendingOrg(null)
    setIsJoining(true)
    try {
      await organizationService.requestToJoin(org.id)
      void notification(NotificationType.Success)
      toast.success(t('org.join_request_sent'))
      onJoined?.(org.id)
      onClose()
    } catch (err) {
      if (__DEV__) console.error('[JoinOrgSheet]', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('errors.generic')))
      captureError(err, { screen: 'join_org', action: 'request_to_join' }, { orgId: org.id })
    } finally {
      setIsJoining(false)
    }
  }, [pendingOrg, onClose, onJoined, t])

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
      testID="org.join-sheet"
    >
      <LiquidCanvas
        isolate
        style={{
          flex: 1,
          paddingTop: insets.top,
        }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: SPACING.lg,
            paddingVertical: SPACING.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            gap: SPACING.md,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontSize: 20,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('org.join')}
            </Text>
          </View>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            testID="org.join-close"
            hitSlop={8}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: colors.surfaceSubtle,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color={colors.textSecondary} strokeWidth={2} />
          </Pressable>
        </View>

        {/* Search input */}
        <View style={{ paddingHorizontal: SPACING.lg, paddingVertical: SPACING.md }}>
          <GlassSurface
            tone="quiet"
            style={{
              height: 48,
              borderRadius: RADIUS.md,
            }}
            contentStyle={{
              flexDirection: 'row',
              alignItems: 'center',
              height: 48,
              paddingHorizontal: SPACING.md,
              gap: SPACING.sm,
            }}
          >
            <Search size={18} color={colors.textMuted} strokeWidth={1.5} />
            <RNTextInput
              ref={inputRef}
              value={query}
              onChangeText={handleQueryChange}
              placeholder={t('org.join_search_placeholder')}
              placeholderTextColor={colors.textMuted}
              autoCorrect={false}
              autoCapitalize="none"
              style={{
                flex: 1,
                fontSize: 16,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
                height: 48,
              }}
              testID="org.join-search-input"
            />
          </GlassSurface>
        </View>

        {/* Results */}
        <RNScrollView
          contentContainerStyle={{
            paddingHorizontal: SPACING.lg,
            paddingBottom: insets.bottom + SPACING.lg,
          }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {isSearching && (
            <View style={{ gap: SPACING.md, paddingTop: SPACING.sm }}>
              {[0, 1, 2].map((i) => (
                <View
                  key={i}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}
                >
                  <Skeleton width={44} height={44} borderRadius={RADIUS.md} />
                  <View style={{ flex: 1, gap: SPACING.xs }}>
                    <Skeleton width="60%" height={14} />
                    <Skeleton width="80%" height={12} />
                  </View>
                </View>
              ))}
            </View>
          )}

          {!isSearching && hasSearched && results.length === 0 && (
            <View style={{ paddingVertical: SPACING.xl, alignItems: 'center' }}>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('org.join_no_results')}
              </Text>
            </View>
          )}

          {!isSearching &&
            results.map((org) => (
              <Pressable
                key={org.id}
                onPress={() => handleJoinRequest(org)}
                disabled={isJoining}
                accessibilityRole="button"
                accessibilityLabel={org.name}
                accessibilityState={{ disabled: isJoining }}
                testID={`org.join-result-${org.id}`}
                style={{ marginBottom: SPACING.sm, opacity: isJoining ? 0.6 : 1 }}
              >
                <GlassSurface
                  style={{
                    borderRadius: RADIUS.lg,
                  }}
                  contentStyle={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: SPACING.md,
                    gap: SPACING.md,
                  }}
                >
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: RADIUS.md,
                      backgroundColor: `${COLORS.purple.DEFAULT}12`,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Building2 size={22} color={COLORS.purple.DEFAULT} strokeWidth={1.5} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                          flexShrink: 1,
                        }}
                        numberOfLines={1}
                      >
                        {org.name}
                      </Text>
                      {org.isVerified && (
                        <ShieldCheck size={14} color={colors.success} strokeWidth={2} />
                      )}
                    </View>
                    {org.legalName ? (
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: SPACING.xs,
                        }}
                        numberOfLines={1}
                      >
                        {org.legalName}
                      </Text>
                    ) : null}
                  </View>
                  {org.memberCount !== undefined && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
                      <Users size={13} color={colors.textMuted} strokeWidth={1.8} />
                      <Text
                        style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}
                      >
                        {org.memberCount}
                      </Text>
                    </View>
                  )}
                </GlassSurface>
              </Pressable>
            ))}
        </RNScrollView>
      </LiquidCanvas>
      <ConfirmDialog
        visible={!!pendingOrg}
        title={t('org.join_confirm_title')}
        message={pendingOrg ? t('org.join_confirm_desc', { name: pendingOrg.name }) : undefined}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        destructive={false}
        confirmLoading={isJoining}
        onConfirm={() => void confirmJoinRequest()}
        onCancel={() => setPendingOrg(null)}
        testIDPrefix="org.join"
      />
    </AppModal>
  )
}
